<!--
  The attachment picker (14.3): a chip list of what is attached, and an attach button.

  ONE outer *ngIf, reading ONE getter. A practice with no documents gets nothing; a practice whose
  documents could not be READ gets the row and the error, because those two look identical to a
  user and only one of them is worth retrying. See the component header.

  Chips are hand-drawn rather than `mat-chip`. This component is embedded in hosts across two
  feature modules whose own Karma TestBeds import Material module by module, and a `mat-chip-set`
  here would silently become a template error in two suites this phase does not own. A span, a
  span and a button owe nobody a module.
-->
<div class="dap" *ngIf="isVisible" data-test="picker">

    <div class="dap__header">
        <span class="dap__label" data-test="picker-label">{{ label }}</span>

        <!--
          The COUNT beside the heading, where the old block's own heading used to say it.

          What is attached is already legible — the chips are right there and each one names
          itself. A block restating "3 selected" under them was saying what the row above already
          said, in more space, in a colour that pulled the eye away from the thing it described.
        -->
        <span class="dap__count" *ngIf="selectedCount" data-test="picker-count">
            {{ selectedCount }}
        </span>

        <button mat-icon-button type="button" class="dap__attach" color="primary"
            data-test="picker-attach" [disabled]="!canAttach"
            [attr.aria-label]="'Attach documents'" title="Attach documents"
            (click)="openAttachDialog()">
            <mat-icon>attach_file</mat-icon>
        </button>
    </div>

    <!-- A load that failed is not an empty practice. The two say different things, are drawn
         differently, and now differ in whether this control exists at all. -->
    <div class="dap__alert dap__alert--error" *ngIf="loadError" data-test="picker-error">
        <mat-icon>error_outline</mat-icon>
        <div>{{ loadError }}</div>
    </div>

    <!-- The documents loaded; the appointment type's TRIGGERS did not. Nothing was attached
         automatically, and the user can still attach by hand. -->
    <div class="dap__alert dap__alert--warn" *ngIf="triggerError" data-test="picker-trigger-error">
        <mat-icon>schedule</mat-icon>
        <div>{{ triggerError }}</div>
    </div>

    <!--
      NOTHING ATTACHED IS NOT NOTHING TO ATTACH. The practice has documents and this context's
      configuration simply named none of them, which is the case the owner asked for by name: the
      user decides per consent, per invite, per appointment, so the control is here to be used.
    -->
    <p class="dap__hint" *ngIf="isUnattached && !loadError" data-test="picker-none-attached">
        No documents attached. Use the attach button to add one.
    </p>

    <div class="dap__chips" *ngIf="!isUnattached">
        <span class="dap__chip" *ngFor="let chip of chips; trackBy: trackByChip"
            data-test="picker-chip" [class.dap__chip--information]="!chip.requiresResponse">

            <mat-icon class="dap__chip-icon" [title]="chip.responseSentence">
                {{ chip.responseIcon }}
            </mat-icon>

            <span class="dap__chip-text">
                <span class="dap__chip-name" data-test="picker-chip-name">{{ chip.name }}</span>
                <!--
                  24.2 - WHEN a trigger would send it. "3 days before" is the thing a receptionist
                  needs to see before overriding it, and it is on the chip because that is the only
                  place they see this document at all before they send.
                -->
                <span class="dap__chip-trigger" *ngIf="chip.trigger" data-test="picker-chip-trigger">
                    {{ chip.trigger }}
                </span>
            </span>

            <button type="button" class="dap__chip-remove" data-test="picker-chip-remove"
                [attr.data-test-document]="chip.id"
                [attr.aria-label]="'Remove ' + chip.name" [title]="'Remove ' + chip.name"
                (click)="remove(chip.id)">
                <mat-icon>cancel</mat-icon>
            </button>
        </span>
    </div>

    <!--
      WHAT THE SELECTION AS A WHOLE WILL DO (14.4). It stays on the chip row rather than living
      only in the dialog: the dialog closes, and the user still has to be able to see what the send
      they are about to make will actually do.
    -->
    <!--
      THE CONSEQUENCE SENTENCE IS GONE FROM HERE, deliberately, and is NOT gone from the dialog.

      Here it restated the chips beside it. In the dialog it is the only thing that says what a
      SELECTION does rather than what one document is — "one link for two documents, the third
      rides as an attachment" — and that is a fact about the mix which nothing else on that screen
      carries. Removed where it duplicated; kept where it informs.
    -->
</div>
